import { getAxiosApps, postAxiosCreateApp, getAppPolicyEndpoint, postAppPolicyRequest } from "@API/Apps/endpoints";
import { IGetAxiosApps, IPostAxiosApp, NewAppPayload } from "@API/Apps/types";
import { showSnackbar } from "@Store/toast/actions";
import i18n from "i18next";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  addAppPolicy,
  addAppPolicyFail,
  addAppPolicySuccess,
  appsListFail,
  appsListRequest,
  appsListSuccess,
  createAppFail,
  createAppRequest,
  createAppSuccess,
  getAppPolicyFail,
  getAppPolicyRequest,
  getAppPolicySuccess,
  resetCreateAppModal,
} from "./actions";

function isNotFound(e: unknown): boolean {
  return (e as AxiosError)?.response?.status === 404;
}

function* onAppsListRequest() {
  try {
    const response: IGetAxiosApps = yield call(getAxiosApps);
    if (response.error) throw response.error;
    yield put(appsListSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(appsListFail(e as Error));
  }
}

function* onCreateAppRequest({ payload }: PayloadAction<NewAppPayload>) {
  try {
    const response: IPostAxiosApp = yield call(postAxiosCreateApp, payload);
    if (response.error) throw response.error;
    yield put(createAppSuccess(response.data));
    yield put(resetCreateAppModal());
    yield put(
      showSnackbar(i18n.t("apps.modals.addApp.successMessage"), "success")
    );
  } catch (e) {
    yield put(createAppFail(e as Error));
    yield put(
      showSnackbar(i18n.t("apps.modals.addApp.failMessage"), "error", e)
    );
  }
}

export default function* () {
  yield takeLatest(appsListRequest, onAppsListRequest);
  yield takeLatest(createAppRequest, onCreateAppRequest);
  yield takeLatest(getAppPolicyRequest.type, onGetAppPolicyRequest);
  yield takeLatest(addAppPolicy, onAddAppPolicyRequest);
}

function* onGetAppPolicyRequest(
  action: PayloadAction<{ appId: string; type: string }>
) {
  const { appId, type } = action.payload;
  try {
    const response: AxiosResponse = yield call(
      getAppPolicyEndpoint,
      appId,
      type
    );
    yield put(
      getAppPolicySuccess({
        appId,
        type,
        contentEN: response.data.contentEN || "",
        contentDE: response.data.contentDE || "",
      })
    );
  } catch (e) {
    if (isNotFound(e)) {
      yield put(
        getAppPolicySuccess({
          appId,
          type,
          contentEN: "",
          contentDE: "",
        })
      );
      return;
    }
    yield put(getAppPolicyFail(e as Error));
  }
}

function* onAddAppPolicyRequest(
  action: PayloadAction<{
    appId: string;
    type: string;
    contentEN: string;
    contentDE: string;
  }>
) {
  try {
    yield call(postAppPolicyRequest, action.payload);
    yield put(addAppPolicySuccess());
    yield put(
      showSnackbar(i18n.t("apps.policies.successMessage"), "success")
    );
    yield put(
      getAppPolicyRequest({
        appId: action.payload.appId,
        type: action.payload.type === "PRIVACY" ? "privacy" : "gtac",
      })
    );
  } catch (e) {
    yield put(addAppPolicyFail());
    yield put(
      showSnackbar(i18n.t("apps.policies.failMessage"), "error", e)
    );
  }
}
